/**
 * THE GOOD LORD — theme behaviour
 *
 * Header scroll state, product gallery, variant selection, quantity
 * steppers, AJAX add-to-cart, cart quantity updates, localization and
 * sort auto-submit, and the data-sale opt-out control.
 *
 * Everything degrades to a working server-rendered form if JS fails.
 */

(function () {
  'use strict';

  function parseJSONScript(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn('Theme: could not parse JSON payload', err);
      return null;
    }
  }

  // Shopify injects routes via content_for_header, but guard for locale
  // subpaths and any case where the global has not been set.
  function routeRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  // ============================================================
  //  HEADER — border appears once the page has scrolled
  // ============================================================
  (function header() {
    var el = document.getElementById('SiteHeader');
    if (!el) return;

    if (el.dataset.sticky === 'false') {
      el.style.position = 'static';
    } else {
      // Re-assert sticky on both desktop and mobile; overflow fixes live in CSS.
      el.style.position = 'sticky';
      el.style.top = '0';
    }

    var ticking = false;

    function update() {
      el.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

    update();
  })();

  // ============================================================
  //  MOBILE MENU
  //  The <details> element opens on its own, so this only adds the
  //  dismissals a native disclosure does not give us.
  // ============================================================
  (function mobileMenu() {
    var menu = document.getElementById('HeaderMenu');
    if (!menu) return;

    var toggle = menu.querySelector('summary');

    function close() {
      if (menu.open) menu.open = false;
    }

    document.addEventListener('click', function (event) {
      if (menu.open && !menu.contains(event.target)) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !menu.open) return;
      close();
      if (toggle) toggle.focus();
    });

    // The panel is display:none above the breakpoint, so an open menu would
    // otherwise reappear when the viewport shrinks again.
    window.addEventListener('resize', function () {
      if (menu.open && !window.matchMedia('(max-width: 640px)').matches) close();
    });
  })();

  // ============================================================
  //  CART DRAWER
  //  Open state is a class on <body>, which lets the panel's markup be
  //  replaced wholesale by the Section Rendering API without the drawer
  //  closing or losing its place.
  // ============================================================
  var cartDrawer = (function () {
    var SECTION = 'cart-drawer';
    var FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    var lastFocus = null;

    function panel() {
      return document.getElementById('CartDrawer');
    }

    function isOpen() {
      return document.body.classList.contains('cart-drawer-open');
    }

    function focusables() {
      var el = panel();
      if (!el) return [];
      return Array.prototype.slice.call(el.querySelectorAll(FOCUSABLE)).filter(function (node) {
        return node.offsetWidth > 0 || node.offsetHeight > 0;
      });
    }

    function open(trigger) {
      if (!panel()) return;

      if (!isOpen()) {
        lastFocus = trigger || document.activeElement;
        document.body.classList.add('cart-drawer-open', 'no-scroll');
      }

      var close = panel().querySelector('[data-cart-drawer-close]');
      if (close) close.focus();
    }

    function close() {
      if (!isOpen()) return;

      document.body.classList.remove('cart-drawer-open', 'no-scroll');

      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    }

    // Swap in freshly rendered markup, keeping focus inside the panel if it
    // was there before the swap.
    function render(html) {
      var wrapper = document.getElementById('shopify-section-' + SECTION);
      if (!wrapper || !html) return;

      var hadFocus = wrapper.contains(document.activeElement);
      var incoming = new DOMParser()
        .parseFromString(html, 'text/html')
        .getElementById('shopify-section-' + SECTION);

      wrapper.innerHTML = incoming ? incoming.innerHTML : html;

      if (hadFocus) {
        var closeButton = wrapper.querySelector('[data-cart-drawer-close]');
        if (closeButton) closeButton.focus();
      }
    }

    function change(payload) {
      if (!window.fetch) return Promise.resolve(null);

      payload.sections = SECTION;
      payload.sections_url = window.location.pathname;

      return fetch(routeRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          // A rejected change (out of stock, stale line number) still parses
          // as JSON, so check the status before trusting the payload.
          if (!response.ok) throw new Error('Cart update failed');
          return response.json();
        })
        .then(function (cart) {
          if (cart && cart.sections) render(cart.sections[SECTION]);
          if (cart && typeof cart.item_count === 'number') setCartCount(cart.item_count);
          return cart;
        })
        .catch(function () {
          // Reload so the drawer cannot keep showing a stale cart.
          window.location.reload();
        });
    }

    return {
      section: SECTION,
      isOpen: isOpen,
      focusables: focusables,
      open: open,
      close: close,
      render: render,
      change: change
    };
  })();

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;

    // Without the drawer on the page the link keeps working as a link.
    var trigger = event.target.closest('[data-cart-drawer-open]');
    if (trigger && document.getElementById('CartDrawer')) {
      event.preventDefault();
      cartDrawer.open(trigger);
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      cartDrawer.close();
      return;
    }

    var remove = event.target.closest('[data-cart-drawer-remove]');
    if (remove) {
      var row = remove.closest('[data-line]');
      if (!row || !window.fetch) return;
      event.preventDefault();
      cartDrawer.change({ line: parseInt(row.dataset.line, 10), quantity: 0 });
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!cartDrawer.isOpen()) return;

    if (event.key === 'Escape') {
      cartDrawer.close();
      return;
    }

    if (event.key !== 'Tab') return;

    var items = cartDrawer.focusables();
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-cart-drawer-quantity]')) return;

    var row = event.target.closest('[data-line]');
    if (!row) return;

    var quantity = parseInt(event.target.value, 10);
    if (isNaN(quantity) || quantity < 0) quantity = 0;

    cartDrawer.change({ line: parseInt(row.dataset.line, 10), quantity: quantity });
  });

  // ============================================================
  //  QUANTITY STEPPERS (product page and cart)
  // ============================================================
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-quantity-change]');
    if (!button) return;

    var wrap = button.closest('.quantity-input');
    var field = wrap && wrap.querySelector('input');
    if (!field) return;

    var step = parseInt(button.dataset.quantityChange, 10) || 0;
    var min = parseInt(field.getAttribute('min'), 10);
    if (isNaN(min)) min = 0;

    var next = (parseInt(field.value, 10) || 0) + step;
    if (next < min) next = min;

    field.value = next;
    field.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // ============================================================
  //  CART — quantity changes submit the cart form
  // ============================================================
  (function cart() {
    var form = document.getElementById('CartForm');
    if (!form) return;

    form.addEventListener('change', function (event) {
      if (event.target.matches('[data-cart-quantity]')) {
        form.submit();
      }
    });
  })();

  // ============================================================
  //  PRODUCT — gallery, variants, add to cart
  // ============================================================
  (function product() {
    var root = document.querySelector('[data-product-root]');
    if (!root) return;

    var strings = parseJSONScript(root.querySelector('[data-product-strings]')) || {};
    var variants = parseJSONScript(root.querySelector('[data-variant-json]')) || [];

    var galleryMain = root.querySelector('[data-gallery-main]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    var priceEl = root.querySelector('[data-product-price]');
    var variantIdInput = root.querySelector('[data-variant-id]');
    var addButton = root.querySelector('[data-add-to-cart]');
    var addButtonText = root.querySelector('[data-add-to-cart-text]');
    var optionInputs = Array.prototype.slice.call(root.querySelectorAll('[data-variant-option]'));

    function setMainImage(url, alt) {
      if (!galleryMain || !url) return;
      var img = galleryMain.querySelector('img');
      if (!img) return;
      img.removeAttribute('srcset');
      img.src = url;
      if (alt) img.alt = alt;
    }

    // --- Gallery thumbnails ---
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        setMainImage(thumb.dataset.mediaUrl, thumb.dataset.mediaAlt);
        thumbs.forEach(function (other) {
          other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
        });
      });
    });

    // --- Variant selection ---
    function selectedOptions() {
      var values = [];
      optionInputs.forEach(function (input) {
        if (input.checked) {
          var match = input.name.match(/option(\d+)/);
          if (match) values[parseInt(match[1], 10) - 1] = input.value;
        }
      });
      return values;
    }

    function findVariant(values) {
      return (
        variants.filter(function (variant) {
          return values.every(function (value, index) {
            return variant.options[index] === value;
          });
        })[0] || null
      );
    }

    function renderPrice(variant) {
      if (!priceEl) return;

      var onSale = Boolean(variant.compare_at_price);
      var html = '<span class="price' + (onSale ? ' price--on-sale' : '') + '">';
      html += '<span class="price-regular">' + variant.price + '</span>';

      if (onSale) {
        html +=
          '<span class="price-compare"><span class="visually-hidden">' +
          (strings.regularPrice || '') +
          '</span>' +
          variant.compare_at_price +
          '</span>';
      }

      html += '</span>';
      priceEl.innerHTML = html;
    }

    function updateButton(variant) {
      if (!addButton) return;

      var available = variant && variant.available;
      addButton.disabled = !available;

      if (addButtonText) {
        if (!variant) {
          addButtonText.textContent = strings.unavailable || 'Unavailable';
        } else {
          addButtonText.textContent = available
            ? strings.addToCart || 'Add to cart'
            : strings.soldOut || 'Sold out';
        }
      }
    }

    function onOptionChange() {
      var variant = findVariant(selectedOptions());

      updateButton(variant);
      if (!variant) return;

      if (variantIdInput) variantIdInput.value = variant.id;
      renderPrice(variant);

      if (variant.featured_image) {
        setMainImage(variant.featured_image, null);
      }

      // Keep the URL shareable so a refresh preserves the selection.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', onOptionChange);
    });

    // --- AJAX add to cart ---
    var productForm = document.getElementById('ProductForm');

    if (productForm && window.fetch) {
      productForm.addEventListener('submit', function (event) {
        if (!addButton || addButton.disabled) return;

        event.preventDefault();

        var original = addButtonText ? addButtonText.textContent : '';
        addButton.disabled = true;
        if (addButtonText) addButtonText.textContent = '...';

        // Ask for the drawer markup in the same round trip, so it can be
        // revealed already showing the item that was just added.
        var payload = new FormData(productForm);
        payload.append('sections', cartDrawer.section);
        payload.append('sections_url', window.location.pathname);

        fetch(routeRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: payload
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Add to cart failed');
            return response.json();
          })
          .then(function (data) {
            if (addButtonText) addButtonText.textContent = 'Added';
            if (data && data.sections) cartDrawer.render(data.sections[cartDrawer.section]);
            cartDrawer.open(addButton);
            return refreshCartCount();
          })
          .then(function () {
            window.setTimeout(function () {
              addButton.disabled = false;
              if (addButtonText) addButtonText.textContent = original;
            }, 1600);
          })
          .catch(function () {
            // Fall back to the standard form POST, which Shopify handles.
            productForm.submit();
          });
      });
    }
  })();

  // ============================================================
  //  CART COUNT
  // ============================================================
  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });

    // The mobile icon badge is hidden rather than showing a zero.
    document.querySelectorAll('[data-cart-count-badge]').forEach(function (el) {
      el.classList.toggle('is-empty', count === 0);
    });
  }

  function refreshCartCount() {
    if (!window.fetch) return Promise.resolve();

    return fetch(routeRoot() + 'cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        setCartCount(cart.item_count);
      })
      .catch(function () {
        /* Count stays as server-rendered. */
      });
  }

  // ============================================================
  //  AUTO-SUBMIT SELECTS (localization, collection sort)
  // ============================================================
  document.addEventListener('change', function (event) {
    var select = event.target;

    if (select.matches('[data-localization-select]')) {
      var localizationForm = select.closest('form');
      if (localizationForm) localizationForm.submit();
      return;
    }

    if (select.matches('[data-auto-submit]')) {
      var sortForm = select.closest('form');
      if (sortForm) sortForm.submit();
    }
  });

  // ============================================================
  //  DATA SALE OPT-OUT
  //  Only surfaced for visitors in a region that requires it, which the
  //  Customer Privacy API determines at runtime rather than in Liquid.
  // ============================================================
  (function dataSaleOptOut() {
    var control = document.querySelector('[data-data-sale-opt-out]');
    if (!control) return;

    function wire(api) {
      if (!api || typeof api.saleOfDataRegion !== 'function') return;
      if (!api.saleOfDataRegion()) return;

      control.hidden = false;

      control.addEventListener('click', function () {
        api.setTrackingConsent({ sale_of_data: false }, function () {
          control.textContent = 'Preference saved';
          control.disabled = true;
        });
      });
    }

    if (window.Shopify && window.Shopify.customerPrivacy) {
      wire(window.Shopify.customerPrivacy);
      return;
    }

    if (window.Shopify && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (error) return;
          wire(window.Shopify.customerPrivacy);
        }
      );
    }
  })();
})();
